// @givmo/board — what each officer title does in Givmo (CBG-YR3-ROLES layer 3, D-CBG-21 (3);
// backend contract `board-officer-roles.md` §14; the `l3-*` frames).
//
// A board's DUTIES tie Board's own acts to its titles: who presides, who signs the minutes, who keeps
// the minute book, who sends notices and the packet, who presents the financial report. Givmo's
// starting point does nothing until an Owner adopts it; every change is a new adoption, and the
// earlier ones stay in the history. One duty changes what Board does — whom it asks to sign approved
// minutes — and the rest are labels and suggestions a person confirms. NONE OF IT decides who may act
// (plan §3.2): anyone on the roster may still sign, any officer with access may still schedule or
// send. The words here never say "must" or what the law asks of an office.
//
// Decoded tolerantly off `unknown`: a field missing, a status or act this build doesn't know, or a
// malformed row never throws — a row with no act is dropped, an unknown act is kept and named as served.

import type { BoardOfficerDutyRef } from '@givmo/api-client';
import { officerRolesL3Words as l3 } from './officer-roles-l3-words';
import type { CatalogTitle, OfficerCatalogRow } from './officer-roles';

/** Board's own acts, in the order a board reads them (the backend's catalog order). */
export const DUTY_ACTS = [
  'presides',
  'presides_in_absence',
  'signs_minutes',
  'keeps_minute_book',
  'gives_notice',
  'presents_financial_report',
] as const;

/** The one duty that changes what Board does: whom it asks to sign approved minutes. */
export const SIGNS_MINUTES = 'signs_minutes';

/** One duty as the read serves it. All four title fields are null for one "not assigned". */
export interface DutyLine {
  act_key: string;
  title_id: string | null;
  title_default_key: string | null;
  title_label: string | null;
  title_status: string | null;
  bylaw_citation: string | null;
}

/** One adoption: who adopted it and when; on an earlier one, when it was replaced and its duties. */
export interface DutyAdoption {
  id: string;
  adopted_at: string | null;
  adopted_by_display_name: string | null;
  superseded_at: string | null;
  duties: DutyLine[];
}

/** The read: the adoption in force (null before the first), the duties in force — else Givmo's
 *  starting point — and the earlier adoptions, newest first. */
export interface OfficerDuties {
  adopted: DutyAdoption | null;
  duties: DutyLine[];
  earlier: DutyAdoption[];
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function decodeLine(value: unknown): DutyLine | null {
  const row = record(value);
  const act = row === null ? null : text(row.act_key);
  if (row === null || act === null) return null;
  return {
    act_key: act,
    title_id: text(row.title_id),
    title_default_key: text(row.title_default_key),
    title_label: text(row.title_label)?.trim() ?? null,
    title_status: text(row.title_status),
    bylaw_citation: text(row.bylaw_citation)?.trim() ?? null,
  };
}

function decodeLines(value: unknown): DutyLine[] {
  return (Array.isArray(value) ? value : [])
    .map(decodeLine)
    .filter((line): line is DutyLine => line !== null);
}

function decodeAdoption(value: unknown): DutyAdoption | null {
  const row = record(value);
  const id = row === null ? null : text(row.id);
  if (row === null || id === null) return null;
  return {
    id,
    adopted_at: text(row.adopted_at),
    adopted_by_display_name: text(row.adopted_by_display_name)?.trim() ?? null,
    superseded_at: text(row.superseded_at),
    duties: decodeLines(row.duties),
  };
}

/** The duties read, decoded tolerantly (an absent or malformed body reads as nothing adopted and
 *  no duties). */
export function extractOfficerDuties(body: unknown): OfficerDuties {
  const row = record(body);
  if (row === null) return { adopted: null, duties: [], earlier: [] };
  return {
    adopted: decodeAdoption(row.adopted),
    duties: decodeLines(row.duties),
    earlier: (Array.isArray(row.earlier) ? row.earlier : [])
      .map(decodeAdoption)
      .filter((adoption): adoption is DutyAdoption => adoption !== null),
  };
}

/** The words for an act: the catalog's, or the served key humanized for one this build doesn't
 *  know. */
export function dutyWords(act: string): string {
  return l3.duty[act] ?? act.replace(/_/g, ' ');
}

/** Whether a duty is held by a title someone can hold: a title is named and it is active. A
 *  starting-point duty whose default title is retired, or an adopted one whose title was retired
 *  since, is "not assigned to a title". */
export function isAssigned(line: DutyLine): boolean {
  return line.title_label !== null && (line.title_status ?? 'active') === 'active';
}

function actOrder(act: string): number {
  const index = (DUTY_ACTS as readonly string[]).indexOf(act);
  return index === -1 ? DUTY_ACTS.length : index;
}

/** The stable key a title is grouped and chosen by: its stored id, else its default's key. */
export function lineTitleKey(line: DutyLine): string | null {
  if (line.title_id !== null) return line.title_id;
  return line.title_default_key !== null ? `default:${line.title_default_key}` : null;
}

/** Where a duty is written: the board's own citation, else — once adopted — Givmo's starting point;
 *  before adoption nothing (the card's line already says the whole list is the starting point). */
export function dutySource(line: DutyLine, adopted: boolean): string | null {
  if (line.bylaw_citation !== null) return l3.sourceCited(line.bylaw_citation);
  return adopted ? l3.sourceStart : null;
}

/** One title's duties, as the Officers part and the member page show them. */
export interface DutyGroup {
  key: string;
  /** What a row's test id names it by: the stored id, else the default's key (the catalog's `ref`). */
  ref: string;
  label: string;
  lines: DutyLine[];
  /** The source every line shares, said once under the group; undefined when they differ (each
   *  line then says its own). */
  sharedSource: string | null | undefined;
}

/**
 * The duties grouped by title, in the board's order of titles (the catalog's), each group's lines
 * in the catalog's order of acts; then every act no title does — "not assigned to a title": one the
 * board's set leaves out (an adoption stores only the duties it gives a title), one whose title was
 * retired since, or a starting-point duty whose default title is retired.
 */
export function dutyGroups(
  duties: readonly DutyLine[],
  adopted: boolean,
  catalog: readonly OfficerCatalogRow[] = [],
): { groups: DutyGroup[]; unassigned: DutyLine[] } {
  const place = (key: string): number => {
    const index = catalog.findIndex((row) =>
      row.id !== null ? row.id === key : `default:${row.default_key}` === key,
    );
    return index === -1 ? catalog.length : index;
  };
  const byTitle = new Map<string, DutyGroup>();
  const firstSeen: string[] = [];
  const assignedActs = new Set<string>();
  for (const line of duties) {
    const key = lineTitleKey(line);
    if (!isAssigned(line) || key === null) continue;
    assignedActs.add(line.act_key);
    if (!byTitle.has(key)) {
      byTitle.set(key, {
        key,
        ref: line.title_id ?? line.title_default_key ?? key,
        label: line.title_label ?? '',
        lines: [],
        sharedSource: undefined,
      });
      firstSeen.push(key);
    }
    byTitle.get(key)?.lines.push(line);
  }
  const groups = firstSeen
    .map((key) => byTitle.get(key) as DutyGroup)
    .sort(
      (a, b) => place(a.key) - place(b.key) || firstSeen.indexOf(a.key) - firstSeen.indexOf(b.key),
    )
    .map((group) => {
      const lines = [...group.lines].sort((a, b) => actOrder(a.act_key) - actOrder(b.act_key));
      const sources = [...new Set(lines.map((line) => dutySource(line, adopted)))];
      return { ...group, lines, sharedSource: sources.length === 1 ? sources[0] : undefined };
    });
  const unassigned = [...new Set([...DUTY_ACTS, ...duties.map((line) => line.act_key)])]
    .filter((act) => !assignedActs.has(act))
    .sort((a, b) => actOrder(a) - actOrder(b))
    .map((act) => ({
      act_key: act,
      title_id: null,
      title_default_key: null,
      title_label: null,
      title_status: null,
      bylaw_citation: null,
    }));
  return { groups, unassigned };
}

/**
 * What one title does in Givmo, as the member page says it under the title (`l3-member-duties`): the
 * ADOPTED duties only — Givmo's starting point does nothing until the board uses it — grouped by
 * where each is written, each group's duties in the catalog's order. Empty before adoption, or for a
 * title the duties don't name.
 */
export function titleDutiesBySource(
  read: OfficerDuties,
  titleId: string,
): { source: string; duties: string[] }[] {
  if (read.adopted === null) return [];
  const lines = read.duties
    .filter((line) => isAssigned(line) && line.title_id === titleId)
    .sort((a, b) => actOrder(a.act_key) - actOrder(b.act_key));
  const groups: { source: string; duties: string[] }[] = [];
  for (const line of lines) {
    const source = dutySource(line, true) ?? l3.sourceStart;
    const group = groups.find((candidate) => candidate.source === source);
    if (group === undefined) groups.push({ source, duties: [dutyWords(line.act_key)] });
    else group.duties.push(dutyWords(line.act_key));
  }
  return groups;
}

/** An adoption's body for the duties as read: each assigned duty by its title's id, or — for a
 *  default nobody has used yet — by its key, with its citation; one not assigned is left out. */
export function adoptionRefs(duties: readonly DutyLine[]): BoardOfficerDutyRef[] {
  const refs: BoardOfficerDutyRef[] = [];
  for (const line of duties) {
    if (!isAssigned(line)) continue;
    const act = line.act_key as BoardOfficerDutyRef['act_key'];
    let ref: BoardOfficerDutyRef;
    if (line.title_id !== null) ref = { act_key: act, title_id: line.title_id };
    else if (line.title_default_key !== null)
      ref = {
        act_key: act,
        default_key: line.title_default_key as NonNullable<BoardOfficerDutyRef['default_key']>,
      };
    else continue;
    refs.push(line.bylaw_citation !== null ? { ...ref, bylaw_citation: line.bylaw_citation } : ref);
  }
  return refs;
}

/** An earlier adoption's duties by title, for its history row: each title as the adoption named it
 *  — whatever has happened to the title since — with its duties in the catalog's order. */
export function adoptionGroups(adoption: DutyAdoption): { label: string; duties: string[] }[] {
  const groups: { key: string; label: string; acts: string[] }[] = [];
  for (const line of adoption.duties) {
    const key = lineTitleKey(line) ?? line.title_label ?? '';
    const label = line.title_label ?? '';
    if (label.length === 0) continue;
    const group = groups.find((candidate) => candidate.key === key);
    if (group === undefined) groups.push({ key, label, acts: [line.act_key] });
    else group.acts.push(line.act_key);
  }
  return groups.map((group) => ({
    label: group.label,
    duties: [...group.acts].sort((a, b) => actOrder(a) - actOrder(b)).map(dutyWords),
  }));
}

// ── "Change": the duties dialog ─────────────────────────────────────────────────────────────────

/** The titles the dialog offers: the board's active titles, in its order, each by its stored id or —
 *  for a default nobody has used yet — `default:<key>`, the same key a duty line is chosen by. */
export function dutyTitleChoices(catalog: readonly OfficerCatalogRow[]): CatalogTitle[] {
  return catalog
    .filter((row) => !row.retired)
    .map((row) => ({
      key: row.id ?? `default:${row.default_key}`,
      id: row.id,
      default_key: row.default_key,
      label: row.label,
    }));
}

/** The dialog's "No title" choice. A title's choice key is its id or `default:<key>`, never this. */
export const NO_TITLE = 'no-title';

/** One act's row in the dialog: the chosen title (a catalog choice key, or `NO_TITLE`) and the
 *  board's own citation. */
export interface DutyDraft {
  act_key: string;
  title: string;
  citation: string;
}

/** The dialog's starting rows: every act of the catalog, each with the title it is assigned to now
 *  and its citation, or "No title". The dialog gives each act one title, as the frames draw it, and
 *  nothing else writes the duties (no tool does, §14.4), so no adopted set gives an act two; were one
 *  to, its first would be kept. */
export function dutyDrafts(duties: readonly DutyLine[]): DutyDraft[] {
  return DUTY_ACTS.map((act) => {
    const line = duties.find((candidate) => candidate.act_key === act && isAssigned(candidate));
    const key = line === undefined ? null : lineTitleKey(line);
    return {
      act_key: act,
      title: key ?? NO_TITLE,
      citation: line?.bylaw_citation ?? '',
    };
  });
}

/** The adoption body the dialog's rows say: each act with a title, by the title's stored id or a
 *  default's key, and its citation when one is written. */
export function draftRefs(
  drafts: readonly DutyDraft[],
  choices: readonly CatalogTitle[],
): BoardOfficerDutyRef[] {
  const refs: BoardOfficerDutyRef[] = [];
  for (const draft of drafts) {
    if (draft.title === NO_TITLE) continue;
    const choice = choices.find((candidate) => candidate.key === draft.title);
    if (choice === undefined) continue;
    const act = draft.act_key as BoardOfficerDutyRef['act_key'];
    const ref: BoardOfficerDutyRef =
      choice.id !== null
        ? { act_key: act, title_id: choice.id }
        : {
            act_key: act,
            default_key: choice.default_key as NonNullable<BoardOfficerDutyRef['default_key']>,
          };
    const citation = draft.citation.trim();
    refs.push(citation.length > 0 ? { ...ref, bylaw_citation: citation } : ref);
  }
  return refs;
}
